import { GreenCheck } from '@assets/icons';
import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import { Colors } from '@constants/colors.constants';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Stack, Typography, alpha } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';
import { AuditOfferSupplierLightDto } from '@store/audits/audit-offer-followUp/audit-proposal-suppliers/audit-proposal-suppliers.model';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { finalize, switchMap } from 'rxjs';
import { AuditOfferUpdateDto } from '../../../../store/audits/audit-offer/audit-offer.model';
import { auditsOfferService } from '../../../../store/audits/audit-offer/audit-offer.services';


enum DetailStatusEnum {
  ACCEPTED = 'ACCEPTED',
  NONE = 'NONE',
  DENIED = 'DENIED',
}

interface SelectSupplierProps {
  handleCloseModal: () => void;
  auditOfferId?: number;
  selectedOfferSupplierId?: number;
}

const SelectSupplier = (props: SelectSupplierProps) => {
  const { handleCloseModal, auditOfferId, selectedOfferSupplierId } = props;
  const { t } = useTranslation();
  const [selectedSupplierRow, setSelectedSupplierRow] = useState<number | null>(selectedOfferSupplierId || 1);
  const [supplierData, setSupplierData] = useState<AuditOfferSupplierLightDto[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [isloading, setIsLoading] = useState(false);
  const { auditId } = useParams();

  useEffect(() => {
    if (auditOfferId) {
      setIsLoading(true);
      auditsOfferService.GETAuditsOffersSuppliersByAuditOfferId(auditOfferId).pipe(
          finalize(() => setIsLoading(false))
      ).subscribe({
        next: (response) => {
          setSupplierData(response);
        },
        error: () => {
          enqueueSnackbar(t('errors.genericError'), { variant: 'error' });
        },
      });
    }
  }, [auditOfferId, t, enqueueSnackbar]);

  const items = [
    { label: 'Accepté', value: DetailStatusEnum.ACCEPTED, color: Colors.success, backgroundColor: '#58e06d33', },
    { label: 'En attente de réponse', value: DetailStatusEnum.NONE, color: Colors.warning, backgroundColor: '#F2A93B', },
    { label: 'Refusé', value: DetailStatusEnum.DENIED, color: Colors.error, backgroundColor: '#F44336', },

  ];

  const getLabels = (value: string) => {
    const item = items.find((it) => it.value === value);
    return item?.label;
  };

  const handleSelectedProposalRows = (params: any) => {
    console.log(params);
    setSelectedSupplierRow(params[0]);
  };

  const handleValidate = () => {
    if (selectedSupplierRow !== null) {
        const row = supplierData.find((row) => row.id === selectedSupplierRow);
      if (auditOfferId) {
        const payload: AuditOfferUpdateDto = {
          selectedAuditOfferSupplierId: row?.id,
          isInitialOffer: row?.isInitialOffer,
        };
        auditsOfferService.PatchAuditOfferById(auditOfferId.toString(), payload).pipe(
            finalize(() => handleCloseModal()),
            switchMap(() => auditsOfferService.GETAuditOffersByAudit(auditId as string))
        ).subscribe({
          error: (err) => enqueueSnackbar(err.text, err.options),
          next: () => {
            enqueueSnackbar(t('audits.success.offer.price-group-action'), { variant: 'success' });
            handleCloseModal();
          },
        });
      }
    }
  };

  return (
      <ModalComponent
          aria-label="over-drawer"
          title={t('audits.OfferFormalization.selectSupplier.title')}
          handleClose={handleCloseModal}
          customColor='#FFFFFF'
          contentHeight='45dvh'
          contentFullWidth
          maxWidth='lg'
          content={(
              <Stack width="100%" height={'100%'} bgcolor='#FFFFFF' justifyContent='center' alignItems='center' spacing={5}>
                <NewTableComponent
                    columns={[
                      COLUMN_TYPES.TEXT({
                        field: 'selectedSupplierName',
                        width: 350,
                        color: 'secondary',
                        label: t('audits.OfferFormalization.selectSupplier.supplier'),
                      }),
                        COLUMN_TYPES.TEXT({
                            field: 'serviceKind',
                            color: 'secondary',
                            label: 'Prestation',
                            width: 350,
                        }),
                      COLUMN_TYPES.NUMBER({
                        field: 'unitCostInCents',
                        label: t('audits.OfferFormalization.selectSupplier.price'),
                        sortable: true,
                        valueGetter: (params: GridValueGetterParams<any, AuditOfferSupplierLightDto>) => params.row?.unitCostInCents,
                        valueFormatter: (params) => params?.value ? `${(params?.value / 100).toFixed(2)} CHF` : '0 CHF',
                        width: 200,
                      }),
                      COLUMN_TYPES.CUSTOM({
                        field: 'status',
                        width: 230,
                        color: '#FFFFF',
                        label: t('audits.OfferFormalization.selectSupplier.status'),
                        renderCell: ({ value }) => (
                            <Stack
                                direction='row'
                                alignItems='center'
                                sx={{
                                  gap: 1,
                                  borderRadius: '4px',
                                  padding: '4px 14px',
                                  backgroundColor: value === DetailStatusEnum.ACCEPTED ? '#58e06d33' : value === DetailStatusEnum.NONE ? alpha('#F2A93B', 0.14) : value === DetailStatusEnum.DENIED ? alpha('#E6433C', 0.14) : undefined,
                                }}
                            >
                              {value === DetailStatusEnum.ACCEPTED ? <GreenCheck /> :
                                  value === DetailStatusEnum.NONE ? <AccessTimeIcon sx={{ color: '#F2A93B', width: '16px', height: '16px' }} /> :
                                      value === DetailStatusEnum.DENIED ? <CloseIcon sx={{ color: '#F44336', width: '16px', height: '16px' }} /> :
                                          undefined}
                              <Typography fontSize={'14px'} fontWeight={400} color={value === DetailStatusEnum.ACCEPTED ? Colors.success : value === DetailStatusEnum.NONE ? Colors.warning : Colors.error}>
                                {getLabels(value)}
                              </Typography>
                            </Stack>
                        ),
                      }),
                    ]}
                    disablePagination
                    disableRowSelectionOnClick={true}
                    disableEdition={true}
                    loading={isloading}
                    isRowSelectable={(params) => { return params.row.status === DetailStatusEnum.ACCEPTED; }}
                    handleRowSelected={handleSelectedProposalRows}
                    rowsSelected={selectedSupplierRow ? [selectedSupplierRow] : []}
                    fullScreen
                    data={supplierData || []}
                    singleRowSelection
                />
                <Button
                    sx={{ width: '100px', alignSelf: 'center', bottom: '40px' }}
                    variant="contained"
                    color="primary"
                    disabled={selectedSupplierRow === null}
                    onClick={handleValidate}
                >
                  {t('global.validate')}
                </Button>
              </Stack>
          )}
      />
  );
};

export default SelectSupplier;